import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import MetaPixel from './global/metapixel';
import { FaCheckCircle, FaAmazon, FaExternalLinkAlt, FaTimes, FaMobileAlt, FaBolt } from 'react-icons/fa';

const REDIRECT_URL = 'https://www.amazon.com/Stone-Heart-Jute-Foster-ebook/dp/B0H4HWVXZK?maas=maas_adg_82B1824605C4DD65F04511E02FC9BC72_afap_abs&ref_=aa_maas&tag=maas';
const AMAZON_APP_SCHEME = 'com.amazon.mobile.shopping.web://amazon.com/dp/B0H4HWVXZK?maas=maas_adg_82B1824605C4DD65F04511E02FC9BC72_afap_abs&ref_=aa_maas&tag=maas';
const ANDROID_INTENT_URL = `intent://www.amazon.com/Stone-Heart-Jute-Foster-ebook/dp/B0H4HWVXZK?maas=maas_adg_82B1824605C4DD65F04511E02FC9BC72_afap_abs&ref_=aa_maas&tag=maas#Intent;scheme=https;package=com.amazon.mShop.android.shopping;S.browser_fallback_url=${encodeURIComponent(REDIRECT_URL)};end;`;
const ANDROID_CHROME_URL = `intent://www.amazon.com/Stone-Heart-Jute-Foster-ebook/dp/B0H4HWVXZK?maas=maas_adg_82B1824605C4DD65F04511E02FC9BC72_afap_abs&ref_=aa_maas&tag=maas#Intent;scheme=https;package=com.android.chrome;end;`;

function getEnvironment(searchParams) {
  if (typeof navigator === 'undefined') {
    return { isFB: false, isIG: false, isInApp: false, isIOS: false, isAndroid: false };
  }
  const ua = navigator.userAgent || navigator.vendor || '';
  let isFB = /FBAN|FBAV|FB_IAB|FBIOS/i.test(ua);
  let isIG = /Instagram/i.test(ua);
  let isIOS = /iPad|iPhone|iPod/i.test(ua) && !(typeof window !== 'undefined' && window.MSStream);
  let isAndroid = /Android/i.test(ua);

  // Allow query parameter override for testing in any browser (e.g. ?inapp=ios or ?inapp=android or ?inapp=1)
  const inappParam = searchParams ? searchParams.get('inapp') : null;
  if (inappParam) {
    if (inappParam === 'android') {
      isAndroid = true;
      isIOS = false;
      isFB = true;
    } else if (inappParam === 'ios' || inappParam === 'ig' || inappParam === 'instagram') {
      isIOS = true;
      isAndroid = false;
      isIG = true;
    } else {
      isFB = true;
      isIOS = true;
    }
  }

  return {
    isFB,
    isIG,
    isInApp: isFB || isIG,
    isIOS,
    isAndroid,
  };
}

const StoneHeartInterstitial = () => {
  const [searchParams] = useSearchParams();
  const [env, setEnv] = useState({ isFB: false, isIG: false, isInApp: false, isIOS: false, isAndroid: false });
  const [showNotice, setShowNotice] = useState(true);

  useEffect(() => {
    setEnv(getEnvironment(searchParams));
  }, [searchParams]);

  const handleBreakout = useCallback((destinationType = 'default') => {
    // 1. Gather tracking identifiers
    let fbc = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_fbc='))
      ?.split('=')[1];

    if (!fbc) {
      const fbclid = searchParams.get('fbclid');
      if (fbclid) {
        fbc = 'fb.1.' + Date.now() + '.' + fbclid;
      }
    }
    if (fbc === undefined) {
      fbc = '';
    }

    const eventIdVal = Date.now();
    const query = new URLSearchParams({
      ts: eventIdVal,
      c: btoa(fbc),
      u: window.location.href,
    }).toString();

    // 2. Fire CAPI log
    try {
      fetch('/api/log?' + query, { keepalive: true }).catch(() => { });
    } catch (e) {
      // ignore
    }

    // 3. Fire Client Pixel
    if (window.fbq) {
      window.fbq(
        'track',
        'InitiateCheckout',
        {
          content_name: 'Stone Heart',
          content_category: 'Fantasy Romance',
          currency: 'USD',
        },
        { eventID: eventIdVal }
      );
    }

    // 4. Execute breakout navigation based on platform
    if (destinationType === 'chrome' && env.isAndroid) {
      window.location.href = ANDROID_CHROME_URL;
      return;
    }

    if (destinationType === 'web') {
      window.location.href = REDIRECT_URL;
      return;
    }

    // Default 1-Tap Breakout:
    if (env.isAndroid) {
      // On Android, use the package intent which opens Amazon app,
      // or cleanly falls back to Chrome outside of the Facebook webview
      window.location.href = ANDROID_INTENT_URL;
    } else if (env.isIOS) {
      // On iOS inside FB/IG, launch the Amazon shopping app custom scheme
      // which triggers iOS's "Open in Amazon?" prompt.
      const startTime = Date.now();
      let appOpened = false;

      const handleVisibilityChange = () => {
        if (document.hidden) {
          appOpened = true;
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });

      window.location.href = AMAZON_APP_SCHEME;

      // Fallback timer: If Amazon app isn't installed and the app didn't open,
      // navigate to the Amazon web URL after 1.8s
      setTimeout(() => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (!appOpened && !document.hidden && Date.now() - startTime < 3000) {
          window.location.href = REDIRECT_URL;
        }
      }, 1800);
    } else {
      // Desktop / Standard browser: Direct redirect
      window.location.href = REDIRECT_URL;
    }
  }, [env, searchParams]);

  const coverImageSrc = '/assets/images/stoneheart_hero.jpg';

  return (
    <section className="stoneheart-interstitial">
      <MetaPixel />

      {/* Subtle indicator for iOS In-App Browsers pointing to top-right menu dots */}
      {env.isInApp && env.isIOS && showNotice && (
        <div className="shi-corner-cue">
          <span className="shi-corner-cue__label">Tap ••• for Safari</span>
          <span className="shi-corner-cue__arrow">↗</span>
        </div>
      )}

      <div className="container">
        {/* In-App Browser Callout Banner */}
        {env.isInApp && showNotice && (
          <div className="shi-iab-notice" role="alert">
            <div className="shi-iab-notice__icon">
              <FaMobileAlt />
            </div>
            <div className="shi-iab-notice__content">
              <strong>Reading in {env.isIG ? 'Instagram' : 'Facebook'}?</strong>
              {env.isIOS ? (
                <>
                  {' '}To avoid typing your password, tap below to open in the <strong>Amazon App</strong>, or tap <span className="shi-iab-notice__step">•••</span> in the top right corner and choose <span className="shi-iab-notice__step">Open in Safari</span> for 1-Click reading.
                </>
              ) : (
                <>
                  {' '}Tap below to open directly in the <strong>Amazon App</strong> or <strong>Chrome</strong> without having to log in again!
                </>
              )}
            </div>
            <button
              className="shi-iab-notice__dismiss"
              onClick={() => setShowNotice(false)}
              aria-label="Dismiss banner"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Header / Brand */}
        <header className="shi-header">
          <div className="shi-header__author">Jute Foster</div>
          <h1 className="shi-header__title">STONE HEART</h1>
        </header>

        {/* Hero Hook (Matches Ad Visuals!) */}
        <div className="shi-hook">
          <div className="shi-hook__badge">A Little Western • ALL Fantasy Romance</div>
          <h2 className="shi-hook__lead">
            It looks different because <span className="highlight">IT IS</span>
          </h2>
          <p className="shi-hook__sub">
            Out on the frontier, the laws of nature are written in <span className="accent">salt and blood</span>.
          </p>
        </div>

        {/* 3D Book Cover Showcase */}
        <div className="shi-cover-wrapper">
          <div className="shi-cover-card">
            <img
              src={coverImageSrc}
              alt="Stone Heart by Jute Foster - Book Cover"
              className="img-fluid"
            />
            <div className="shi-cover-ku-pill">
              <span className="ku-label">Available on</span>
              <span className="ku-brand">kindle</span>
              <span className="ku-unlimited">unlimited</span>
            </div>
          </div>
        </div>

        {/* The 1-Tap Primary CTA */}
        <div className="shi-action-box">
          <button
            onClick={() => handleBreakout('default')}
            className="shi-main-btn"
            id="stone-heart-primary-cta"
          >
            <span className="shi-main-btn__top">
              <FaBolt /> Read Free on Kindle Unlimited
            </span>
            <span className="shi-main-btn__sub">
              👉 1-Tap to Open in Amazon App / Browser
            </span>
          </button>

          <div className="shi-secondary-links">
            <span>Also available in Paperback • </span>
            <button
              onClick={() => handleBreakout('web')}
              className="link-btn"
            >
              Open in Web Browser <FaExternalLinkAlt style={{ fontSize: '10px' }} />
            </button>
            {env.isAndroid && (
              <>
                {' '}•{' '}
                <button
                  onClick={() => handleBreakout('chrome')}
                  className="link-btn"
                >
                  Open in Chrome
                </button>
              </>
            )}
          </div>
        </div>

        {/* Trope Callouts (Continuity with Facebook/Instagram Ad) */}
        <div className="shi-tropes-section">
          <div className="shi-tropes-section__title">What Readers Are Calling Addictive</div>
          <div className="shi-tropes-grid">
            <div className="shi-trope-card">
              <div className="shi-trope-card__icon">
                <FaCheckCircle />
              </div>
              <div className="shi-trope-card__content">
                <h5>Genius FMC</h5>
                <p>Field Naturalist Neris: armed with a razor-sharp intellect and stubborn courage.</p>
              </div>
            </div>

            <div className="shi-trope-card">
              <div className="shi-trope-card__icon">
                <FaCheckCircle />
              </div>
              <div className="shi-trope-card__content">
                <h5>Broken MMC</h5>
                <p>Stone: scarred former monster hunter, drowned in ghosts and whiskey, the frontier's most lethal protector.</p>
              </div>
            </div>

            <div className="shi-trope-card">
              <div className="shi-trope-card__icon">
                <FaCheckCircle />
              </div>
              <div className="shi-trope-card__content">
                <h5>Opposites Attract</h5>
                <p>High-tension, forced-proximity slow burn across a toxic, lawless wasteland where heat builds with every mile.</p>
              </div>
            </div>

            <div className="shi-trope-card">
              <div className="shi-trope-card__icon">
                <FaCheckCircle />
              </div>
              <div className="shi-trope-card__content">
                <h5>Corrupted Magic & Beasts</h5>
                <p>Ethereal spirit dragons, deadly alchemical horrors, and a relentless Concord Marshal closing in on their trail.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Story Blurb & High Stakes */}
        <div className="shi-blurb-card">
          <div className="shi-blurb-card__quote">
            "When a rogue alchemist murders her father for his research, Neris faces an impossible choice: let her family starve, or march into the toxic desert to steal it back."
          </div>
          <p>
            Field Naturalist Neris knows all about the wastes, but she’s never had to survive the reality. Armed only with intelligence and stubbornness, she hires the one man capable of keeping her alive.
          </p>
          <p>
            Except Stone is little more than a ghost. Once a legendary monster hunter, his present is at the bottom of a bottle. Neris is everything he can't ignore—headstrong, fiercely intelligent, and his one shot at redemption.
          </p>
        </div>

        {/* Secondary Bottom CTA */}
        <div className="shi-bottom-cta">
          <button
            onClick={() => handleBreakout('default')}
            className="shi-main-btn"
          >
            <span className="shi-main-btn__top">
              <FaAmazon /> Open Stone Heart on Amazon
            </span>
            <span className="shi-main-btn__sub">
              Available on Kindle Unlimited & Paperback
            </span>
          </button>
        </div>
      </div>

      {/* Floating Sticky Bottom Bar for Mobile Viewports */}
      <aside className="shi-sticky-bar" aria-label="Quick purchase bar">
        <div className="shi-sticky-bar__info">
          <img
            src={coverImageSrc}
            alt="Stone Heart Thumbnail"
          />
          <div className="shi-sticky-bar__text">
            <div className="title">Stone Heart</div>
            <div className="badge-sub">Kindle Unlimited & Amazon</div>
          </div>
        </div>
        <button
          onClick={() => handleBreakout('default')}
          className="shi-sticky-bar__btn"
          id="stone-heart-sticky-cta"
        >
          ⚡ Read Now (1-Tap)
        </button>
      </aside>
    </section>
  );
};

export default StoneHeartInterstitial;
